/**
 * LCFPIGovernanceTab.js — Governance Tab
 * Wraps: Government Workspace, Audit Trail, Institutional Reports,
 *        Settings & Governance, Public Registry, Pipeline Digest
 */
import React, { useState, Suspense, lazy } from "react";
import { Globe, Shield, BarChart3, Settings, Database, BookOpen } from "lucide-react";
import { LCFPIErrorBoundary } from "@/components/lcfpi/LCFPIPageShell";

const LCFPIGovWorkspace  = lazy(() => import("./LCFPIGovWorkspace"));
const LCFPIAuditTrail    = lazy(() => import("./LCFPIAuditTrail"));
const LCFPIReports       = lazy(() => import("./LCFPIReports"));
const LCFPISettings      = lazy(() => import("./LCFPISettings"));
const LCFPIPublicRegistry= lazy(() => import("./LCFPIPublicRegistry"));
const LCFPIDigest        = lazy(() => import("./LCFPIDigest"));

const SECTIONS = [
  { id: "gov",      label: "Government Workspace",   icon: Globe,     accent: "#d97706", desc: "Programme oversight"        },
  { id: "audit",    label: "Audit Trail",            icon: Shield,    accent: "#1e40af", desc: "Verification & governance"  },
  { id: "reports",  label: "Institutional Reports",  icon: BarChart3, accent: "#8b5cf6", desc: "Portfolio reporting"         },
  { id: "registry", label: "Public Registry",        icon: Database,  accent: "#059669", desc: "Published project data"     },
  { id: "digest",   label: "Pipeline Digest",        icon: BookOpen,  accent: "#0891b2", desc: "Intelligence briefings"     },
  { id: "settings", label: "Settings",               icon: Settings,  accent: "#64748b", desc: "Platform configuration"     },
];

const Loader = () => (
  <div className="flex items-center justify-center py-24">
    <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

export default function LCFPIGovernanceTab() {
  const [active, setActive] = useState("gov");
  const sec = SECTIONS.find(s => s.id === active) || SECTIONS[0];

  return (
    <div style={{ minHeight: "calc(100vh - 88px)", background: "#060d1a" }}>
      <div style={{ background: "#07101e", borderBottom: "1px solid #1e293b", padding: "0 24px", display: "flex", gap: 2, alignItems: "center", overflowX: "auto" }}>
        {SECTIONS.map(s => {
          const isActive = active === s.id; const Icon = s.icon;
          return (
            <button key={s.id} data-testid={`lcfpi-gov-sub-${s.id}`}
              onClick={() => setActive(s.id)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 14px", background: isActive ? `${s.accent}18` : "transparent", border: "none", borderBottom: `2px solid ${isActive ? s.accent : "transparent"}`, color: isActive ? "#fff" : "#64748b", fontSize: 11, fontWeight: isActive ? 700 : 500, cursor: "pointer", whiteSpace: "nowrap", transition: "all .15s" }}>
              <Icon size={11} color={isActive ? s.accent : "currentColor"} />
              {s.label}
            </button>
          );
        })}
      </div>
      <div style={{ padding: "12px 24px", borderBottom: "1px solid #0f172a", background: "#060d1a" }}>
        <p style={{ fontSize: 10, color: "#475569", textTransform: "uppercase", letterSpacing: ".08em" }}>
          <span style={{ color: sec.accent, fontWeight: 700 }}>Governance</span> › {sec.label} · {sec.desc}
        </p>
      </div>
      <LCFPIErrorBoundary>
        <Suspense fallback={<Loader />}>
          <div style={{ padding: "20px 24px" }}>
            {active === "gov"      && <LCFPIGovWorkspace />}
            {active === "audit"    && <LCFPIAuditTrail />}
            {active === "reports"  && <LCFPIReports />}
            {active === "registry" && <LCFPIPublicRegistry />}
            {active === "digest"   && <LCFPIDigest />}
            {active === "settings" && <LCFPISettings />}
          </div>
        </Suspense>
      </LCFPIErrorBoundary>
    </div>
  );
}
